import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { placeholderTypes } from '../../../constants/placeholder-types.constants';
import { OtaObject } from '../../../core/interfaces/property';
import { IElfsightDynamicSourceType } from './constants';

@Component({
  selector: 'app-review-widget',
  templateUrl: './review-widget.component.html',
  styleUrls: ['./review-widget.component.scss'],
  standalone: false
})
export class ReviewWidgetComponent implements OnInit, AfterViewInit {
  isElfsightLoaded = false;
  placeholderTypes = placeholderTypes;

  @ViewChild('reviewsContentElement') reviewsContentElement;
  @Input() classList: string;
  @Input() minified: boolean;
  @Input() otas: OtaObject[];
  @Output() hideReviewsWidget: EventEmitter<void> = new EventEmitter<void>();
  @Output() reviewsWidgetClicked = new EventEmitter<void>();

  hasAssignedSources: boolean;
  assignedSources: string;
  starRating: string;
  totalReviews: string;

  elfsightBadgeWidgetId = 'elfsight-app-e1e15736-fb47-4b3d-903d-daa42b3eee26';
  elfsightCarouselWidgetId = 'elfsight-app-4e651dd4-913b-4592-bd41-d8837a7e3b86';

  elfsightScript = this.document.createElement('script');

  constructor(
    @Inject(DOCUMENT) public document: Document,
    private readonly el: ElementRef,
    private readonly renderer: Renderer2
  ) {
    if (!document.getElementById('reviewWidgetScript')) {
      this.loadElfsight();
    }
  }

  ngOnInit(): void {
    if (window.MutationObserver) {
      const observer = new MutationObserver((mutations, obs) => {
        if (this.reviewsContentElement.nativeElement.childNodes[0]) {
          setTimeout(() => {
            this.isElfsightLoaded = true;
          }, 1500);
          obs.disconnect();
          return;
        }
      });

      observer.observe(document, {
        childList: true,
        subtree: true
      });
    }

    // Filter out OTAs with unsupported Elfsight types
    const supportedOtas = this.otas?.filter((obj) => obj.type in IElfsightDynamicSourceType);

    // Map the remaining supported OTAs
    const elfsightDynamicSources = supportedOtas?.map((obj) => ({
      type: IElfsightDynamicSourceType[obj.type],
      url: obj.link
    }));

    this.hasAssignedSources = true;
    this.assignedSources = JSON.stringify(elfsightDynamicSources);
  }

  ngAfterViewInit(): void {
    this.elfsightScript.onload = () => {
      this.checkForLoading();
    };
  }

  loadElfsight() {
    this.elfsightScript.id = 'reviewWidgetScript';
    this.elfsightScript.src = 'https://apps.elfsight.com/p/platform.js';
    this.elfsightScript.type = 'text/javascript';
    this.document.head.appendChild(this.elfsightScript);
  }

  checkForLoading() {
    const maxWaitTime = 5000; // 5 seconds in milliseconds
    const pollInterval = 500; // Check every 500 milliseconds

    let elapsedTime = 0;

    const checkElement = () => {
      const badgeContainer = this.document?.getElementsByClassName('es-rating-container');
      const tabsContainer = this.document?.getElementsByClassName('es-tabs-container');
      const containerToCheck = this.minified ? badgeContainer.length : tabsContainer.length;

      if (!containerToCheck && elapsedTime < maxWaitTime) {
        elapsedTime += pollInterval;
        setTimeout(checkElement, pollInterval);
      } else if (containerToCheck) {
        this.hideMissingReviews();
      } else {
        this.hideReviewsWidget.emit();
      }
    };

    checkElement();
  }

  hideMissingReviews() {
    // BADGE
    if (this.minified && !this.document?.getElementsByClassName('es-rating-value')?.length) {
      return this.hideReviewsWidget.emit();
    }

    // CAROUSEL
    const tabs = this.document?.getElementsByClassName('es-tabs-slider-item') as HTMLCollectionOf<HTMLElement>;

    if (tabs && !this.minified) {
      let tabsWithRatingCount = 0;

      for (let i = 0; i < tabs.length; i++) {
        const tab = tabs[i];

        if (tab.getElementsByClassName('es-rating-value').length) {
          tabsWithRatingCount = tabsWithRatingCount + 1;
        }
      }

      if (tabsWithRatingCount === 2) {
        // This is necessary to remove the "All Reviews" tab which Elfsight displays by default when there is more than one source
        tabs[0].style.display = 'none';
      }

      if (!tabsWithRatingCount) {
        return this.hideReviewsWidget.emit();
      }

      this.resizeCarouselTabs();
    }
  }

  resizeCarouselTabs() {
    const tabsContainer = this.document?.getElementsByClassName('es-tabs-container')[0] as HTMLElement | undefined;

    if (tabsContainer && window.innerWidth >= 1400) {
      tabsContainer.style.width = 'fit-content';
    }
  }

  reviewsClicked(event) {
    this.reviewsWidgetClicked.emit();
    event.preventDefault();
  }
}
